/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-19 15:17:38                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-26 16:33:58                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * User: user details view
    * useEffect: get user information
    * useEffect: Set breadcrumb
    * updateTechnician: update technician information
*/

/* Imports */
import { t } from "i18next";
import Loading from "../../components/loading/Loading";
import { useEffect, useState } from "react";
import { Alert, Card, Typography } from "@material-tailwind/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
/***/

/* Components */
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import TechnicianForm, { TechnicianFormValidator } from "../../components/data/technician/TechnicianForm";
import ColorDisplay from "../../components/data/color/ColorDisplay";
import AutoDiagTable from "../../components/data/auto-diag/AutoDiagTable";
import { AutoDiagTechnicianStatus, isLateForAutoDiag } from "../../services/auto-diag/autoDiagStatus";
/***/

/* Styles */
import "./User.scss";
import SwitchLanguage from "./SwitchLanguage";
import TablePageSizePreference from "./TablePageSizePreference";
import { useKeycloak } from "@react-keycloak/web";
/***/

/** Absent value marker, matching the em dash the Auto Diag tables already use for empty cells. */
const EMPTY = "—";

/**
* InfoField: one labelled row of the Informations card.
*
* Always renders, empty or not. The block used to hide a field whose value was missing, which left
* the reader unable to tell "no phone recorded" from "this screen does not show phones" — and made
* the card a different shape per technician.
*/
function InfoField({ label, children }: { label: string, children?: React.ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children ?? <Typography>{EMPTY}</Typography>}
    </div>
  );
}
/***/

/**
* User: user details view
* display detailed information for user
*/
export function User() {
  const [user, setUser] = useState<EasyTech.User>();
  const [path, setPath] = useState<{name: string, path: string}[]>([]);
  const { id } = useParams();
  const { keycloak } = useKeycloak();
  const [autoDiagStatus, setAutoDiagStatus] = useState<AutoDiagTechnicianStatus | undefined>();
  
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const userType = searchParams.get("userType");
  const isLate = isLateForAutoDiag(autoDiagStatus);
  const dueDate = autoDiagStatus?.nextDue && !isNaN(new Date(autoDiagStatus.nextDue).getTime())
    ? format(new Date(autoDiagStatus.nextDue), "dd/MM/yyyy")
    : null;

  /**
  * useEffect: get user informations 
  * on init and on id change
  */
  useEffect(() => {
    if (id) {
      if (userType && userType === "TECHNICIAN") {
        api.get(`admin/technicians/${id}`)
          .then((ret) => setUser(ret as EasyTech.User))
          .catch((err) => console.error(err));
      } else {
        api.get(`admin/users/${id}`)
          .then((ret) => setUser(ret as EasyTech.User))
          .catch((err) => console.error(err));
      }
    }
  }, [id, userType]);
  /***/
  
  /**
  * useEffect: get this technician's Auto Diag standing
  *
  * The endpoint lists only technicians who are not up to date, so finding them in it is what says
  * they are late. Fetching the whole (short) list to look up one is the cost of not adding an
  * endpoint for it. A failure means no banner — never a blocked page.
  */
  useEffect(() => {
    if (!id || userType !== "TECHNICIAN") return;
    api.get("admin/auto-diag/technicians")
      .then((ret) => setAutoDiagStatus(
        (ret as AutoDiagTechnicianStatus[]).find((row) => row.technicianId === id)))
      .catch((err) => console.error(err));
  }, [id, userType]);
  /***/

  /**
  * useEffect: Set breadcrumb
  * @param user - reload on user change
  */
  useEffect(() => {
    if (user) {
      setPath([{
        name: t("common:users"),
        path: userType === "TECHNICIAN" ? "/users?tab=technicians" : "/users"
      }, {
        name: `${user.firstName} ${user.lastName}`,
        path: `/users/${user.id}`
      }]);
    }
  }, [user, userType]);
  /***/

  /**
  * updateTechnician: update technician information
  * @param tech - technician data to update
  */
  const updateTechnician = (tech: EasyTech.Technician): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(tech, TechnicianFormValidator)) {
        api.put(`admin/technicians/${tech.id}`, tech).then((ret) => {
          setUser(ret as EasyTech.User);
          toast.success("Technician updated");
          return resolve();
        }).catch((error) => {
          console.error(error);
          showApiErrorToast(error);
          return reject();
        });
      } else {
        toast.error("Form is invalid");
        return reject();
      }
    });
  };
  /***/

  return (
    <div className="user-details">
      {user ?
        <>
          <Breadcrumb path={path}/>

            <div className="flex justify-between items-center">
              <Typography variant="h2">
                {user.firstName} {user.lastName}
              </Typography>

              {user.roles.includes("ROLE_TECH") && (
                <EditCreateDrawer data={(user as EasyTech.Technician)}
                                  entityName="Technician"
                                  FormComponent={TechnicianForm}
                                  isNotCreatable={true}
                                  onSubmit={updateTechnician}/>
              )}
            </div>

          {isLate && (
            <Alert color="red" icon={<ExclamationTriangleIcon className="w-6" />} className="my-4">
              {autoDiagStatus?.blocked ? t("common:autoDiag.lateBlocked") : t("common:autoDiag.late")}
              {dueDate && ` (${t("common:autoDiag.dueOn", { date: dueDate })})`}
            </Alert>
          )}

          <Typography variant="h4" className="sub-title">
          {t("Informations")}
          </Typography>

          <Card className="details-card">
            {/*
              * Mirrors the edit drawer (TechnicianForm), field for field, and every one of them
              * renders whether or not it holds a value — that is the point of the block: a reader
              * comparing two technicians should see the same rows, differing only in content.
              */}
            <InfoField label={t("common:firstName")}>
              {user.firstName ? <Typography>{user.firstName}</Typography> : undefined}
            </InfoField>

            <InfoField label={t("common:lastName")}>
              {user.lastName ? <Typography>{user.lastName}</Typography> : undefined}
            </InfoField>

            <InfoField label={t("common:email")}>
              {user.email ? <Typography>{user.email}</Typography> : undefined}
            </InfoField>

            <InfoField label={t("common:roles")}>
              {user.roles?.length ? <Typography>{user.roles.join(", ")}</Typography> : undefined}
            </InfoField>

            <InfoField label={t("common:language")}>
              {user.language?.label ? <Typography>{user.language.label}</Typography> : undefined}
            </InfoField>

            {user.userType === "TECHNICIAN" && (
              <>
                {/* attributes is optional on the entity, so read through it rather than gating on
                  * it — gating is what used to make phone and colour disappear entirely. */}
                <InfoField label={t("common:phone")}>
                  {(user as EasyTech.Technician).attributes?.phone
                    ? <Typography>{(user as EasyTech.Technician).attributes.phone}</Typography>
                    : undefined}
                </InfoField>

                <InfoField label={t("common:color")}>
                  <ColorDisplay value={(user as EasyTech.Technician).attributes?.color} />
                </InfoField>

                <InfoField label={t("technician:van")}>
                  {(user as EasyTech.Technician).van?.name
                    ? <Typography>{(user as EasyTech.Technician).van?.name}</Typography>
                    : undefined}
                </InfoField>
              </>
            )}

            {user.id === keycloak.tokenParsed?.sub && (
              <>
                <div className="field">
                  <label>{t("common:changeLanguage")}</label>

                  <SwitchLanguage user={user} />
                </div>

                {/* Même garde que la langue : une préférence d'affichage ne se règle que sur son
                  * propre profil, jamais depuis la fiche de quelqu'un d'autre. */}
                <div className="field">
                  <label>{t("common:tablePageSize")}</label>

                  <TablePageSizePreference />
                </div>
              </>
            )}
          </Card>

          {user.userType === "TECHNICIAN" && (
            <>
              <Typography variant="h4" className="sub-title">
                {t("admin:autoDiag.title")}
              </Typography>

              {/* Fetches on its own, so a slow or failing records call leaves the Informations
                * block above already rendered and usable. */}
              <AutoDiagTable technicianId={user.id} showTechnicianColumn={false} />
            </>
          )}
        </>
      : <Loading />}
    </div>
  );
}
/***/
